/**
 * useArticleStats.ts
 *
 * Computes (and caches) word count, character count, and other derived stats
 * for an Article. Used by both legacy data-layer-article.client.ts and the
 * new tracking-lib.client.ts so the body is parsed at most once per article
 * object reference.
 *
 * Cache strategy: WeakMap keyed on the article object reference. When the
 * article store hydrates a new article, a new object is created and the old
 * one becomes garbage-collectable, so the cache evicts itself.
 */
import { type Article } from '~/types/article';

export interface ArticleStats {
  wordCount: number;
  characterCount: number;
}

const STATS_CACHE = new WeakMap<Article, ArticleStats>();
const COUNT_TYPES = new Set(['text', 'subhead']);
const HTML_TAG = /<[^>]+>/g;
const WORD_SPLIT = /\s+/;
const NON_WORD = /[^\w\sæøå]/g;

/**
 * Returns word and character counts for the given article, computed from
 * its body's `text` and `subhead` blocks. Cached per article reference.
 *
 * Cost on first call: O(n) where n is the total characters in countable
 * body blocks. Cost on subsequent calls with the same Article object: O(1).
 */
export function getArticleStats (article: Article | null | undefined): ArticleStats {
  if (!article) {
    return { wordCount: 0, characterCount: 0 };
  }

  const cached = STATS_CACHE.get(article);
  if (cached) {
    return cached;
  }

  let wordCount = 0;
  let characterCount = 0;

  /*
   * Behavior is intentionally byte-for-byte identical to the legacy
   * data-layer-article.client.ts so emitted counts stay aligned during the
   * dual-running period. The legacy algorithm:
   *   words = text.replace(/<[^>]+>/g, '').split(/\s+/)
   *         .map(w => w.replace(/[^\w\sæøå]/g, ''))
   *         // (was also .toLowerCase(), unused — dropped)
   *   wordCount      = words.length
   *   characterCount = sum(w.length for w in words)
   */
  const body = article.body || [];
  for (let i = 0; i < body.length; i++) {
    const block = body[i];
    if (!COUNT_TYPES.has(block.type)) { continue; }

    const text = (block.html || block.value || '').replace(HTML_TAG, '');
    const words = text.split(WORD_SPLIT);
    for (let j = 0; j < words.length; j++) {
      const cleaned = words[j].replace(NON_WORD, '');
      wordCount += 1;
      characterCount += cleaned.length;
    }
  }

  const stats: ArticleStats = { wordCount, characterCount };
  STATS_CACHE.set(article, stats);
  return stats;
}

/**
 * Test-only escape hatch. Production code should never need this — the
 * WeakMap evicts entries when articles go out of scope.
 */
export function __clearArticleStatsCache (article?: Article): void {
  if (article) {
    STATS_CACHE.delete(article);
  }
}
